import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import garantiasApi from '../../../api/garantiasApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import GarantiaFormDialog from '../../../sections/admin/garantias/GarantiaFormDialog';

export default function GarantiasListPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingGarantia, setEditingGarantia] = useState(null);
  const [garantiaToDelete, setGarantiaToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchGarantias = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await garantiasApi.getGarantias({
        search: search || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de garantías.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchGarantias();
  }, [fetchGarantias]);

  const handleFormSaved = async () => {
    await fetchGarantias();
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingGarantia(null);
  };

  const handleConfirmDelete = async () => {
    if (!garantiaToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await garantiasApi.deleteGarantia(garantiaToDelete.id);
      enqueueSnackbar('Garantía eliminada correctamente.', { variant: 'success' });
      setGarantiaToDelete(null);
      await fetchGarantias();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar la garantía.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'document_number', headerName: 'N.° de documento', width: 170 },
      {
        field: 'items',
        headerName: 'Producto(s)',
        flex: 1,
        minWidth: 220,
        valueGetter: (value, row) =>
          row.items
            ?.map((item) => {
              const name = item.product?.name || item.description || 'Producto eliminado';
              return item.serie ? `${name} (${item.serie})` : name;
            })
            .join(', ') || '—',
      },
      {
        field: 'observations',
        headerName: 'Observaciones',
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => row.observations || '—',
      },
      {
        field: 'response_days',
        headerName: 'Tiempo de respuesta',
        width: 160,
        valueFormatter: (value) => `${value} días`,
      },
      {
        field: 'date',
        headerName: 'Fecha de venta',
        width: 130,
        valueFormatter: (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'intake_date',
        headerName: 'Fecha de ingreso',
        width: 140,
        valueFormatter: (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => {
              setEditingGarantia(params.row);
              setFormOpen(true);
            }}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setGarantiaToDelete(params.row)}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row" spacing={2}>
        <div>
          <Typography variant="h4">Garantías</Typography>
          <Typography variant="body2" color="text.secondary">
            Reclamos de garantía registrados: producto(s) con su serie, informe de la falla y evidencia fotográfica.
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingGarantia(null);
            setFormOpen(true);
          }}
        >
          Nueva garantía
        </Button>
      </Stack>

      <Card sx={{ p: 2 }}>
        <TextField
          fullWidth
          label="Buscar"
          placeholder="N.° de documento, producto o serie"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Card>

      <Card>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          onRowClick={(params) => {
            setEditingGarantia(params.row);
            setFormOpen(true);
          }}
        />
      </Card>

      <GarantiaFormDialog open={formOpen} onClose={handleCloseForm} onSaved={handleFormSaved} garantia={editingGarantia} />

      <Dialog open={Boolean(garantiaToDelete)} onClose={() => setGarantiaToDelete(null)}>
        <DialogTitle>Eliminar garantía</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar la garantía de{' '}
            <strong>{garantiaToDelete?.document_number}</strong>? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGarantiaToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
